import { IncomingHttpHeaders } from 'node:http'

import { NextApiRequest, NextApiResponse } from 'next'
import getConfig from 'next/config'
import { ImageLoaderProps } from 'next/image'

import type { SearchFormConfig } from '@awaze/design-system.compositions.search-form'
import { getOptimizelyUserId } from '@awaze/mfe-common.lib.optimizely'
import { parseForceDecisionsFromUrlQuery } from '@awaze/mfe-common.lib.optimizely/dist/utils/parseForceDecisionsFromUrlQuery'
import type {
  SearchCriteria,
  SearchFormCriteria,
} from '@awaze/mfe-common.types-and-mocks.site-search-service'
import { isBotFromHeaders } from '@awaze/mfe-common.utils.is-bot-from-headers'

import { GRADE_CAT_IDS } from 'src/constants'
import { useAppConfig } from 'src/context/app-config-provider'

interface CleanObjectOf<DirtyData> {
  data: DirtyData | null | undefined
  filterOutValues: Array<null | undefined | '' | 0>
}

export const cleanObjectOf = <DirtyObject>({
  data,
  filterOutValues,
}: CleanObjectOf<DirtyObject>): Partial<DirtyObject> => {
  if (!data || typeof data !== 'object') {
    return {}
  }

  return Object.entries(data)
    .filter(([, value]) => !filterOutValues.includes(value))
    .reduce<Record<string, unknown>>((obj, [key, value]) => {
      obj[key] = value
      return obj
    }, {}) as Partial<DirtyObject>
}

export function createSlugFromString(
  name: string,
  code: string | number,
): string {
  const formattedName = name
    .replaceAll(' - ', '-')
    .replaceAll(' ', '-')
    .replaceAll("'", '')
    .replaceAll('&rsquo;', '')
    .replaceAll(/[^\w\\-]/g, '')
  const path = `${formattedName}-${code}`
  return path.toLowerCase()
}

export function createIdFromString(str: string): string {
  return str.toLowerCase().replaceAll(' ', '-')
}

export const createInitialValuesFromConfig = (
  searchFormConfig: SearchFormConfig,
): SearchCriteria => {
  const {
    accommodationType,
    adults,
    children,
    dates,
    destination,
    destinationId,
    destinationURL,
    duration,
    flexibility,
    infants,
    pets,
  } = searchFormConfig

  const configWithoutOptional = {
    destination: destination.defaultValue,
    destinationURL: destinationURL.defaultValue,
    destinationId: destinationId.defaultValue,
    ...(adults.max > 0 && { adult: adults.defaultValue }),
    ...(children.max > 0 && { child: children.defaultValue }),
    ...(infants.max > 0 && { infant: infants.defaultValue }),
    ...(pets.max > 0 && { pets: pets.defaultValue }),
    start: dates.defaultValue,
    range: Number(flexibility.defaultValue),
    nights: Number(duration.defaultValue),
    ...(accommodationType?.defaultValue && {
      accommodationType: accommodationType.defaultValue,
    }),
  } as SearchCriteria

  return configWithoutOptional
}

export const capitalise = (word: string): string => {
  return word.charAt(0).toUpperCase() + word.slice(1)
}

export function formatPropertyPageURL(
  domain: string,
  product: string,
  name: string,
  code: string | number,
  queryString: string,
): string {
  return `${domain}/${product}/${createSlugFromString(
    name,
    code,
  )}?${queryString}`
}

export function generatePageTitle(brand: string, location?: string): string {
  if (location) {
    return `${capitalise(brand)} | Book your accommodation in ${capitalise(
      location,
    )} now`
  }
  return `${capitalise(brand)} | Book your accommodation in all regions now`
}

export function generatePageDescription(
  brand: string,
  location?: string,
): string {
  if (location) {
    return `Find hand-picked holiday cottages in ${capitalise(
      location,
    )} at ${capitalise(brand)}. Compare cottages or book online.`
  }
  return `Find hand-picked holiday cottages in All Regions at ${capitalise(
    brand,
  )}. Compare cottages or book online.`
}

export function getAssetUrl(path: string): string {
  const { assetPrefix } = getConfig()
  return assetPrefix ? `${assetPrefix}/public${path}` : `${path}`
}

export function buildPublicAssetUrl(
  path: string,
  assetPrefix?: string,
): string {
  return assetPrefix ? `${assetPrefix}/public${path}` : `${path}`
}

export function usePublicAssetUrl(path: string): string {
  const { ASSET_PREFIX } = useAppConfig()
  return buildPublicAssetUrl(path, ASSET_PREFIX)
}

export function getImageAssetUrl({ src, width }: ImageLoaderProps): string {
  const imageOpts = `${src}?w=${width}`

  return getAssetUrl(imageOpts)
}

export function scrollTo(element: Element): void {
  element?.scrollIntoView({ behavior: 'smooth' })
}

export function setCookie(
  cname: string,
  cvalue: number | string,
  cexpires?: string,
): void {
  if (globalThis.window === undefined) return
  const expiresString = cexpires ? 'expires=' + cexpires : ''
  document.cookie = `${cname} = ${cvalue}; path=/; ${expiresString}`
}

export function getCookie(cname: string): string | null {
  if (globalThis.window === undefined) return null
  const cookie = new RegExp('(^|;) ?' + cname + '=([^;]*)(;|$)').exec(
    document.cookie,
  )
  return cookie ? cookie[2] : null
}

export function getEndOfDayAsUTCString(): string {
  const today = new Date()
  const midnight = new Date(
    Date.UTC(
      today.getFullYear(),
      today.getMonth(),
      today.getDate(),
      23,
      59,
      59,
    ),
  )
  return midnight.toUTCString()
}

export function calculateTotalPages(total: number, pageSize: number): number {
  return Math.ceil(total / pageSize)
}

export const getFilterTypeArray = (type: string): Array<string> => {
  return type ? [type] : []
}

export function calculateSelectedFilters(
  selectedFilters: SearchCriteria,
): number {
  let total = 0

  for (const [, filterValue] of Object.entries(selectedFilters)) {
    if (Array.isArray(filterValue)) {
      total += filterValue.length
    } else {
      total += getFilterTypeArray(filterValue).length
    }
  }

  return total
}

export function getMinMaxRating(rating: string): Array<string> {
  const minMaxRating = rating.split('-')
  minMaxRating[1] =
    Number.parseInt(minMaxRating[1]) > 9 ? '10' : `${minMaxRating[1]}.9`

  return minMaxRating
}

export const mapGradeParamToNumber = (grade: string): string | null => {
  for (const [gradeNumber, gradeString] of Object.entries(GRADE_CAT_IDS)) {
    if (gradeString === grade) {
      return gradeNumber
    }
  }
  return null
}

export const setBackButtonConfig = (): void => {
  if (globalThis.window !== undefined) {
    const url = globalThis.window.location.href
    localStorage.setItem('lastSearchData', `Search MFE,${url}`)
    localStorage.setItem('lastSearchFeatures', '')
    setCookie('userSearch', url)
  }
}

export type OldBrand =
  | 'agnostic'
  | 'awaze'
  | 'cottages.com'
  | 'dansommer'
  | 'fincallorca'
  | 'hoseasons'
  | 'jamesVillas'
  | 'novasol'

export const getHeaderString = <T>(
  headers: IncomingHttpHeaders,
  headerName: string,
  defaultReturn: T,
): string | T => {
  const lookupValue = headers[headerName]

  if (lookupValue === undefined) {
    return defaultReturn
  }

  return Array.isArray(lookupValue) ? lookupValue[0] : lookupValue
}

export const getOptimizelyHeadersForOneSearch = (
  req: NextApiRequest,
  res: NextApiResponse,
): Record<string, string> => {
  const userId = getOptimizelyUserId({
    cookies: req.cookies,
    response: res,
  })

  const forcedDecisions = parseForceDecisionsFromUrlQuery(req.query)

  const forcedDecisionString =
    forcedDecisions.length > 0
      ? `${forcedDecisions[0].flagKey}:${forcedDecisions[0].variationKey}`
      : ''

  const isBot = isBotFromHeaders(
    req.headers['user-agent'] || '',
    (req.headers['akamai-bot'] as string) ?? null,
    (req.headers['x-amzn-waf-bot-category'] as string) ?? null,
    (req.headers['x-abuse-info'] as string) ?? null,
  )

  return {
    'x-awaze-user-agent': req.headers['user-agent'] || '',
    'x-awaze-bot-traffic': isBot ? 'true' : 'false',
    ...(userId && { 'x-awaze-opti-id': userId }),
    ...(forcedDecisionString !== '' && {
      'x-force-experiment-variant': `${forcedDecisionString}`,
    }),
  }
}

export const searchCriteriaComparisonString = (
  stateSearchCriteria: SearchFormCriteria,
): string => JSON.stringify(stateSearchCriteria)

export const parseSetCookie = (str: string): Record<string, string> =>
  str
    .split(';')
    .map((v) => v.split('='))
    .reduce<Record<string, string>>((acc, v) => {
      if (v[1]) {
        acc[decodeURIComponent(v[0].trim())] = decodeURIComponent(v[1].trim())
      } else {
        acc[decodeURIComponent(v[0].trim())] = 'true'
      }
      return acc
    }, {})
